import type { OpenEditorType, VSCodeEditorArgs } from "utils/buildExtensionUri";
import { buildExtensionUri } from "utils/buildExtensionUri";
import { openCustomProtocol } from "utils/url";
import { VSCODE_INSIDERS_INSTALL_URL, VSCODE_INSTALL_URL } from "utils/vscode";

import type { UseLegacyCsbApiState } from "./api/useLegacyCsbApi";
import { useAuthToken } from "./useAuthToken";

export interface OpenInVsCodeOptions {
  type?: OpenEditorType;
}
export const editorInfo: Record<
  OpenEditorType,
  { name: string; installUrl: string }
> = {
  vscode: { name: "VS Code", installUrl: VSCODE_INSTALL_URL },
  "vscode-insiders": {
    name: "VS Code Insiders",
    installUrl: VSCODE_INSIDERS_INSTALL_URL,
  },
  cursor: { name: "Cursor", installUrl: "https://www.cursor.com/" },
};

/**
 * Redirects to the VS Code extension URI in the current window.
 * Generates a unique auth token for this transition.
 */
export function useOpenInVsCodeInline({
  editor,
  activeFilepath,
}: {
  editor: VSCodeEditorArgs;
  activeFilepath?: string | null;
}): [
  UseLegacyCsbApiState<() => Promise<string>>,
  (options?: OpenInVsCodeOptions) => Promise<void>,
] {
  const [authTokenState, generateAuthToken] = useAuthToken();

  const open = (options: OpenInVsCodeOptions = {}): Promise<void> => {
    /**
     * Generate a new auth token upon each launch request.
     * This way we always provide a valid fresh token to the extension
     * and don't need to care about the token being invalidated.
     * @see CSB-4873
     */
    return generateAuthToken().then((authToken) => {
      const url = buildExtensionUri({
        editor,
        activeFilepath,
        authToken,

        type: options.type,
      });

      return openCustomProtocol(url);
    });
  };

  return [authTokenState, open];
}
